import * as React from 'react';
import {useContext, useMemo} from 'react';
import {createPortal} from 'react-dom';
import {provider} from '@sail/engine';
import {animate} from '@sail/engine/deprecated';
import * as Theme from '@sail/theme-classic';
import {
  ClassicLayerContext,
  LayerContext,
  useLayer,
  getZIndex,
  RESERVED_MODAL_LAYER,
  ClassicLayerSailNextContext,
} from '@sail/global-state';
import type {LayerContextType, LayerType} from '@sail/global-state';

import {css} from '../../css';
import {view} from '../../view';
import {LayerInterop} from '../../../components/layers/LayerInterop';

export function useCurrentLayer(): LayerContextType | null {
  const layer = useContext(LayerContext);
  return layer == null || layer.type === 'root' ? null : layer;
}

type BackwardsCompatibleProps =
  | {
      /**
       * The type of layer: 'overlay', 'partial', or 'root'. This value
       * determines how the layer is positioned and how it interacts with other
       * layers.
       *
       * @deprecated
       */
      type?: LayerType;

      /**
       * The element to use as the container for this layer. If not specified, the
       * layer will be mounted into the nearest parent layer, or the document body
       * if there is no parent layer.
       */
      container?: HTMLElement | null;
      name?: undefined;
    }
  | {
      /**
       * A specific config for this layer. Layers can be configured on the
       * parent layer (usually the root layer) to explicitly order layers. "modal"
       * is a special system layer which puts the layer on top of everything else
       * and switches modality (disables all interactions underneath it). If not
       * specified, it defaults to a non-modal layer ordered by the latest opened
       * layer.
       */
      name: string;
      type?: undefined;
      container?: undefined;
    };

// Overlay layers apply `contain: content` which requires us to manually specify
// its size. This isolates all layout/paint from the rest of the document which
// improves performance
//
// It's also important that we apply `position: fixed` to overlays. We don't
// want consumers to have to worry about z-indexes, so we apply it here, but we
// also need to create our own stacking context so it actually takes effect.
// This way no matter what you do inside an overlay layer, it will be on top of
// everything else. Partial layers get a z-index, but to avoid being too
// prescriptive, we don't apply any positioning. We assume the consumer will
// apply what they want on the `Layer` component.
const layerStyle = css({
  [Theme.LayerVariants.type.partial]: {
    position: css.value('absolute'),
  },
  [Theme.LayerVariants.type.overlay]: {
    contain: css.value('strict'),
    position: css.value('fixed'),
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
});

// A Layer is responsible for managing a stacking order (see `useLayer`) and
// portalling content to make sure content renders on top of everything else.
// Other features like focus trapping are provided in other components.
export const Layer = view.div.create<BackwardsCompatibleProps>(
  ({type: originalType, name: originalName, container, ...props}) => {
    // We know we set `type` below so force its type
    let type = null as unknown as LayerType;
    let name: string | undefined;

    // If `name` is specified, it always infers `type`. TypeScript will enforce
    // these props to be mutually exclusive.
    if (originalName) {
      type = originalName === RESERVED_MODAL_LAYER ? 'overlay' : 'partial';
      name = originalName;
    } else if (originalType) {
      type = originalType;
      if (originalType === 'overlay') {
        name = RESERVED_MODAL_LAYER;
      }
    } else {
      // Default to overlay
      type = 'overlay';
      name = RESERVED_MODAL_LAYER;
    }

    const parentContext = useContext(LayerContext);
    if (parentContext == null) {
      throw new Error(
        'Parent layer not available; did you render a <RootLayer /> around your app?',
      );
    }

    const isSailClassicContext = useContext(ClassicLayerContext);
    const isClassicSailNextLayer = useContext(ClassicLayerSailNextContext);

    /**
     * Use only for Sail Classic layers in Sail Classic layering context.
     * Sail Next already provides correct `context.container` for node
     */
    const isClassicContext = isSailClassicContext && !isClassicSailNextLayer;

    animate.useTimelineListeners(
      useMemo(
        () => ({
          in() {
            parentContext && parentContext.addLayer(type);
          },

          out() {
            parentContext && parentContext.removeLayer(type);
          },
        }),
        [parentContext, type],
      ),
    );

    const context = useLayer({type, name});
    const zIndex = useMemo(() => getZIndex(context), [context]);

    let portalNode = container || context.container;

    if (isClassicContext) {
      // We can't render partial layers in our container in a Sail
      // Classic context. If we did, you wouldn't be able to render
      // Sail Next tooltips/popovers/etc inside a Sail Classic
      // layer. We explicitly want to support that, so we render it
      // outside the container and customize the z-index to make it
      // play nicely.
      if (type === 'partial') {
        portalNode =
          container || (context.container?.parentNode as HTMLElement);
      } else {
        // eslint-disable-next-line no-console
        console.warn(
          'Warning: a Sail Next overlay was rendered from a Sail Classic ' +
            "overlay. This isn't supported and you may run into problems. " +
            'Sail Next overlays will not render above Sail Classic overlays.',
        );
      }
    }

    // If no portal node, we must be in a server-rendered environment. Render
    // nothing.
    if (portalNode == null) {
      return null;
    }

    const contents = (
      <LayerInterop
        data-overlay-container
        // If the layer count is greater than zero, we add `aria-hidden` to this
        // layer to hide its content from screen readers.
        aria-hidden={context && context.layerCount > 0 ? true : undefined}
        legacyIntents={[
          provider(LayerContext, context),
          props,
          layerStyle,
          css({
            // See above comment on why partial layers in a classic
            // context need special treatment
            zIndex: css.value(
              type === 'partial' && isClassicContext ? 400 : zIndex,
            ),
          }),
          Theme.LayerVariants.assign({type}),
        ]}
        style={{pointerEvents: context.hasOverlay() ? 'none' : 'auto'}}
      />
    );

    return createPortal(contents, portalNode);
  },
);

Layer.displayName = 'Layer';
